import {
  type ComponentFn,
  useCardSessionControls,
  useCardSurface,
  useStateValue,
} from '@littlebird/action-cards-view/react';
import { useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';
import { handleExternalUrlClick } from '@/utils/platform';

import { isFeedSurface } from '../../cardFrame';
import { useCardOpen } from '../../cardOpen';
import { appOfUrl } from '../internals/appOfUrl';
import { useNarrowHostRow } from '../internals/hostRow';
import { useFitsLine } from '../internals/useFitsLine';
import { ICON_CLASS, sizingClass, TEXT_STYLE } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

const protocolOf = (href: string): string | null => {
  try {
    return new URL(href).protocol;
  } catch {
    return null;
  }
};

// `quiet` is the small header link ("Open in Gmail"); `text` is the larger, ordinary
// inline link. Each names its size and colour; the link variant adds the underline.
// Colour, underline, wrapping and hover are `!` because a chat card renders inside
// the message's markdown, whose `.markdown a` rules outrank a single class
const TEXT_TONE_CLASS = `!text-foreground ${TEXT_STYLE.body}`;
const QUIET_TONE_CLASS = `!text-foreground-subtle hover:!text-foreground ${TEXT_STYLE.meta}`;
const LINK_HOVER_CLASS = 'underline-offset-2 hover:!text-foreground-subtle';

// Only https and mailto open; any other address shows as its label alone
export const Link: ComponentFn<typeof catalog, 'Link'> = ({ props }) => {
  const openInCard = useCardOpen();
  const { saveEdits } = useCardSessionControls();
  const protocol = protocolOf(props.href);
  const quiet = props.appearance === 'quiet';
  const showIcon = props.icon !== 'none';
  const surface = useCardSurface();
  const kind = useStateValue<string>('/fields/kind');
  const source =
    isFeedSurface(surface) &&
    (kind === 'todo' || kind === 'info') &&
    quiet &&
    showIcon;
  // A link to an app Littlebird knows reads as that app's mark, named in its tooltip
  const app = source ? appOfUrl(props.href) : null;
  const sourceLabel = app?.name ?? props.label.replace(/^Open /, '');
  const measureRef = useRef<HTMLSpanElement>(null);
  const fits = useFitsLine(measureRef, sourceLabel);
  const iconOnly = useNarrowHostRow() && quiet && showIcon;
  const toneClass = quiet ? QUIET_TONE_CLASS : TEXT_TONE_CLASS;
  // A known app shows its mark alone, as the band's "Open in" does
  const label =
    iconOnly || app ? null : source ? (
      <span className="min-w-0 overflow-hidden" aria-hidden="true">
        <span
          ref={measureRef}
          className="inline-block whitespace-nowrap"
          style={{ visibility: fits ? 'visible' : 'hidden' }}
        >
          {sourceLabel}
        </span>
      </span>
    ) : props.truncate ? (
      <span className="min-w-0 truncate">{props.label}</span>
    ) : (
      props.label
    );
  if (protocol !== 'https:' && protocol !== 'mailto:') {
    return (
      <span
        title={props.truncate ? props.label : undefined}
        className={cn(
          toneClass,
          sizingClass(props.sizing),
          props.truncate && 'block min-w-0 truncate'
        )}
      >
        {props.label}
      </span>
    );
  }
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noreferrer"
      data-card-source={source || undefined}
      aria-label={
        app
          ? `Open in ${app.name}`
          : source || iconOnly
            ? props.label
            : undefined
      }
      title={
        app
          ? `Open in ${app.name}`
          : source || props.truncate || iconOnly
            ? props.label
            : undefined
      }
      className={cn(
        'inline-flex w-fit min-w-0 items-center gap-[var(--ac-gap-xs)]',
        toneClass,
        '!underline',
        quiet && !props.truncate && !source && 'shrink-0 !whitespace-nowrap',
        source &&
          'justify-end rounded-sm focus-visible:outline focus-visible:outline-1 focus-visible:outline-border-strongest',
        quiet ? 'underline-offset-2' : LINK_HOVER_CLASS,
        app && '!no-underline',
        sizingClass(props.sizing)
      )}
      onClick={(event) => {
        event.preventDefault();
        if (protocol === 'mailto:') handleExternalUrlClick(props.href);
        // A field the click blurred is still saving, so the open waits for that save
        else openInCard(props.href, saveEdits);
      }}
    >
      {app ? (
        <app.icon aria-hidden="true" className={cn(ICON_CLASS, 'shrink-0')} />
      ) : null}
      {label}
      {showIcon && !app ? (
        <Icons.arrowUpRight
          className={cn(ICON_CLASS, 'shrink-0')}
          aria-hidden="true"
        />
      ) : null}
    </a>
  );
};
